import { UzDatePicker, UzSelect } from '@utazzo/ui';
import React, { useState } from 'react';

import { isoToLocalDate, toDateInputIso } from '../invoices.helpers';

/**
 * Shared financial-year date-range control for the accounts screens. Every
 * finance list/report defaults to the CURRENT Indian FY (1 Apr - 31 Mar) and
 * offers an FY dropdown to switch years, backed by a plain From/To pair for
 * ad-hoc ranges (which flips the dropdown to "Custom").
 *
 * `from`/`to` are `yyyy-mm-dd` strings (what the report queries expect).
 */
export const fyStartYear = (d: Date): number =>
  d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;

export const fyLabel = (startYear: number): string =>
  `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;

export const CURRENT_FY_START = fyStartYear(new Date());

/** FY start year -> {from, to} as yyyy-mm-dd (1 Apr .. 31 Mar). */
export const fyRangeIso = (startYear: number): { from: string; to: string } => ({
  from: `${startYear}-04-01`,
  to: `${startYear + 1}-03-31`,
});

/** Current FY + the previous 5 years, newest first, plus an "All time" escape. */
export const FY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  ...Array.from({ length: 6 }, (_, i) => {
    const start = CURRENT_FY_START - i;
    return { value: String(start), label: `FY ${fyLabel(start)}` };
  }),
  { value: 'all', label: 'All time' },
];

export interface FyRangeState {
  fy: string;
  from: string;
  to: string;
  setFy: (v: string) => void;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
}

/** State hook: initialises to the current FY. */
export const useFyRange = (): FyRangeState => {
  const init = fyRangeIso(CURRENT_FY_START);
  const [fy, setFy] = useState<string>(String(CURRENT_FY_START));
  const [from, setFrom] = useState<string>(init.from);
  const [to, setTo] = useState<string>(init.to);
  return { fy, from, to, setFy, setFrom, setTo };
};

/** FY dropdown + From/To pickers, wired to a `useFyRange` state. */
export const FyRangeControls = ({ state }: { state: FyRangeState }): React.JSX.Element => {
  const { fy, from, to, setFy, setFrom, setTo } = state;

  const onFy = (value: string): void => {
    setFy(value);
    if (value === 'all') {
      setFrom('');
      setTo('');
    } else {
      const r = fyRangeIso(Number(value));
      setFrom(r.from);
      setTo(r.to);
    }
  };

  return (
    <>
      <UzSelect
        label="Financial year"
        value={fy || undefined}
        onValueChange={onFy}
        options={FY_OPTIONS}
        placeholder="Custom"
        containerClassName="w-44"
      />
      <UzDatePicker
        label="From"
        value={isoToLocalDate(from)}
        onValueChange={(d) => {
          if (d) {
            setFrom(toDateInputIso(d));
            setFy('');
          }
        }}
        containerClassName="w-44"
      />
      <UzDatePicker
        label="To"
        value={isoToLocalDate(to)}
        onValueChange={(d) => {
          if (d) {
            setTo(toDateInputIso(d));
            setFy('');
          }
        }}
        containerClassName="w-44"
      />
    </>
  );
};
